import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronDown,
    Check,
    X,
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
} from 'lucide-react';

/**
 * Icon mapping for pillar icons
 */
const ICON_MAP = {
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
};

/**
 * PillarSelector Component
 * Reusable component for selecting marketing pillars
 *
 * Supports three modes:
 * - dropdown: Classic dropdown selector
 * - pills: Horizontal pill/chip buttons
 * - compact: Small inline selector
 *
 * @param {Object} props
 * @param {Array} props.pillars - Available pillars to select from
 * @param {string|Array} props.value - Selected pillar ID(s)
 * @param {function} props.onChange - Callback when selection changes
 * @param {boolean} props.multiple - Allow multiple selection
 * @param {string} props.mode - Display mode: 'dropdown' | 'pills' | 'compact'
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.showClear - Show clear button
 * @param {boolean} props.disabled - Disable the selector
 * @param {string} props.label - Label text
 * @param {boolean} props.showFunnelStage - Show funnel stage badges
 */
const PillarSelector = ({
    pillars = [],
    value,
    onChange,
    multiple = false,
    mode = 'dropdown',
    placeholder = 'Select pillar...',
    showClear = true,
    disabled = false,
    label,
    showFunnelStage = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Normalize value to array for easier handling
    const selectedIds = Array.isArray(value) ? value : (value ? [value] : []);
    const selectedPillars = pillars.filter(p => selectedIds.includes(p.id));

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (pillar) => {
        if (disabled) return;

        if (multiple) {
            const newValue = selectedIds.includes(pillar.id)
                ? selectedIds.filter(id => id !== pillar.id)
                : [...selectedIds, pillar.id];
            onChange(newValue);
        } else {
            onChange(pillar.id);
            setIsOpen(false);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(multiple ? [] : null);
    };

    const getFunnelStages = (pillar) => {
        const stages = [];
        if (pillar.funnelMapping?.TOFU?.recommended) stages.push('TOFU');
        if (pillar.funnelMapping?.MOFU?.recommended) stages.push('MOFU');
        if (pillar.funnelMapping?.BOFU?.recommended) stages.push('BOFU');
        return stages;
    };

    // Render pill/chip for a pillar
    const renderPillarChip = (pillar, showRemove = false) => {
        const IconComponent = ICON_MAP[pillar.icon] || Lightbulb;
        return (
            <div
                key={pillar.id}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '4px 8px',
                    background: `${pillar.color}15`,
                    border: `1px solid ${pillar.color}40`,
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    color: pillar.color
                }}
            >
                <IconComponent size={12} />
                <span style={{ fontWeight: 500 }}>{pillar.name}</span>
                {showRemove && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(pillar);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px',
                            cursor: 'pointer',
                            display: 'flex',
                            opacity: 0.7
                        }}
                    >
                        <X size={10} />
                    </button>
                )}
            </div>
        );
    };

    // Dropdown mode
    if (mode === 'dropdown') {
        return (
            <div ref={dropdownRef} style={{ position: 'relative' }}>
                {label && (
                    <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        marginBottom: 'var(--space-1)'
                    }}>
                        {label}
                    </label>
                )}
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-bg-primary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        minHeight: '40px'
                    }}
                >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', flex: 1 }}>
                        {selectedPillars.length > 0 ? (
                            selectedPillars.map(p => renderPillarChip(p, multiple))
                        ) : (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>{placeholder}</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                        {showClear && selectedPillars.length > 0 && (
                            <button
                                type="button"
                                onClick={handleClear}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    color: 'var(--color-text-tertiary)'
                                }}
                            >
                                <X size={14} />
                            </button>
                        )}
                        <ChevronDown
                            size={16}
                            style={{
                                color: 'var(--color-text-tertiary)',
                                transform: isOpen ? 'rotate(180deg)' : 'none',
                                transition: 'transform var(--transition-fast)'
                            }}
                        />
                    </div>
                </button>

                {/* Dropdown menu */}
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: 'var(--space-1)',
                        background: 'var(--color-bg-primary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 100,
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}>
                        {pillars.length === 0 ? (
                            <div style={{
                                padding: 'var(--space-3)',
                                color: 'var(--color-text-tertiary)',
                                textAlign: 'center',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                No pillars available
                            </div>
                        ) : (
                            pillars.map(pillar => {
                                const IconComponent = ICON_MAP[pillar.icon] || Lightbulb;
                                const isSelected = selectedIds.includes(pillar.id);
                                const stages = getFunnelStages(pillar);

                                return (
                                    <button
                                        key={pillar.id}
                                        type="button"
                                        onClick={() => handleSelect(pillar)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-3)',
                                            padding: 'var(--space-3)',
                                            background: isSelected ? `${pillar.color}10` : 'transparent',
                                            border: 'none',
                                            borderBottom: '1px solid var(--color-border-light)',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: 'background var(--transition-fast)'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        {/* Icon */}
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: 'var(--radius-md)',
                                            background: `${pillar.color}15`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <IconComponent size={16} style={{ color: pillar.color }} />
                                        </div>

                                        {/* Content */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontWeight: 500,
                                                fontSize: 'var(--font-size-sm)',
                                                marginBottom: '2px'
                                            }}>
                                                {pillar.name}
                                            </div>
                                            {showFunnelStage && stages.length > 0 && (
                                                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                                                    {stages.map(stage => (
                                                        <span
                                                            key={stage}
                                                            style={{
                                                                fontSize: '10px',
                                                                padding: '1px 4px',
                                                                background: stage === 'TOFU' ? 'var(--info-100)' :
                                                                    stage === 'MOFU' ? 'var(--warning-100)' :
                                                                        'var(--success-100)',
                                                                color: stage === 'TOFU' ? 'var(--info-700)' :
                                                                    stage === 'MOFU' ? 'var(--warning-700)' :
                                                                        'var(--success-700)',
                                                                borderRadius: 'var(--radius-sm)'
                                                            }}
                                                        >
                                                            {stage}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Check mark */}
                                        {isSelected && (
                                            <Check size={16} style={{ color: pillar.color }} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Pills mode
    if (mode === 'pills') {
        return (
            <div>
                {label && (
                    <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        marginBottom: 'var(--space-2)'
                    }}>
                        {label}
                    </label>
                )}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--space-2)'
                }}>
                    {pillars.map(pillar => {
                        const IconComponent = ICON_MAP[pillar.icon] || Lightbulb;
                        const isSelected = selectedIds.includes(pillar.id);

                        return (
                            <button
                                key={pillar.id}
                                type="button"
                                onClick={() => handleSelect(pillar)}
                                disabled={disabled}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: isSelected ? `${pillar.color}20` : 'var(--color-bg-tertiary)',
                                    border: `2px solid ${isSelected ? pillar.color : 'transparent'}`,
                                    borderRadius: 'var(--radius-full)',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.6 : 1,
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <IconComponent
                                    size={14}
                                    style={{ color: isSelected ? pillar.color : 'var(--color-text-secondary)' }}
                                />
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? pillar.color : 'var(--color-text-primary)'
                                }}>
                                    {pillar.name}
                                </span>
                                {isSelected && multiple && (
                                    <X size={12} style={{ marginLeft: '2px' }} />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Compact mode
    if (mode === 'compact') {
        const selectedPillar = selectedPillars[0];
        const IconComponent = selectedPillar ? (ICON_MAP[selectedPillar.icon] || Lightbulb) : null;

        return (
            <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: '4px 8px',
                        background: selectedPillar ? `${selectedPillar.color}15` : 'var(--color-bg-tertiary)',
                        border: `1px solid ${selectedPillar ? selectedPillar.color + '40' : 'var(--color-border)'}`,
                        borderRadius: 'var(--radius-full)',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontSize: 'var(--font-size-xs)',
                        color: selectedPillar ? selectedPillar.color : 'var(--color-text-secondary)'
                    }}
                >
                    {IconComponent && <IconComponent size={12} />}
                    <span>{selectedPillar ? selectedPillar.name : placeholder}</span>
                    <ChevronDown size={10} />
                </button>

                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 'var(--space-1)',
                        background: 'var(--color-bg-primary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 100,
                        minWidth: '150px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}>
                        {pillars.map(pillar => {
                            const Icon = ICON_MAP[pillar.icon] || Lightbulb;
                            const isSelected = selectedIds.includes(pillar.id);

                            return (
                                <button
                                    key={pillar.id}
                                    type="button"
                                    onClick={() => handleSelect(pillar)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2)',
                                        background: isSelected ? `${pillar.color}10` : 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontSize: 'var(--font-size-xs)'
                                    }}
                                >
                                    <Icon size={12} style={{ color: pillar.color }} />
                                    <span>{pillar.name}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default PillarSelector;
